// Timestop BO — user avatar upload + badge rendering.
//
// Phase 12.13: users can upload a profile image from the Profile modal.
// The flow is:
//   1. user clicks the (current) initial tile in the profile modal
//   2. openAvatarCropper() reads the file with FileReader, paints a
//      512x512 preview into a <canvas>, lets the user drag a square
//      crop window over it, then on Confirm POSTs the cropped bytes
//      as multipart/form-data to /api/upload_avatar
//   3. server resizes the bytes to <=512x512, computes md5 of the
//      normalized output, writes admin/uploads/avatars/<md5>.<ext>
//      and stores the filename in users.avatar
//   4. on success, paintUserBadge(state) replaces the server-rendered
//      initial tile in the topbar badge with an <img> pointing at
//      /api/avatar?u=<id>. The same image also replaces the initial
//      tile in the open Profile modal.
//
// The topbar badge tile is server-rendered in admin/shell.php; the
// first paint therefore always shows the colored initial. paintUserBadge
// is called after state.currentUser is set, and the JS swaps the tile
// out for an <img> iff the user has uploaded one. This avoids a flash
// of colored-tile-then-image on first paint.

import { apiFetch, type ApiFetchConfig } from './api';
import type { TimestopState } from './state';
import { t } from './i18n';

// Window-level surface so the inline scripts in admin/index.php and
// admin/shell.php can call into the bundle without a direct import.
// Picked up automatically by tsc on the next typecheck.
declare global {
  interface Window {
    openAvatarPicker?: () => void;
    closeAvatarCropper?: () => void;
    confirmAvatarCrop?: (state: TimestopState, config: ApiFetchConfig) => Promise<void>;
    paintUserBadge?: (state: TimestopState) => void;
  }
}

// =====================================================================
// 1. Topbar badge painter
// =====================================================================

// Returns the avatar <div> inside the topbar button, or null if the
// shell hasn't been mounted yet.
function findBadgeTile(): HTMLElement | null {
    const btn = document.getElementById('user-badge-btn');
    if (!btn) return null;
    // The tile is the only <div> child of the button (the chevron is
    // an <svg>). We pick the FIRST div to be defensive against any
    // future addition of trailing decorations.
    return btn.querySelector('div');
}

// Returns the avatar element inside the Profile modal (an <img> or
// a <div> with text). Used by the profile-modal's "click to upload"
// flow.
function findProfileInitial(): HTMLElement | null {
    return document.getElementById('profile-initial');
}

// avatarUrl: build the same-origin URL the <img src> will hit. The
// /api/avatar endpoint is auth-gated but session cookies are sent
// automatically on same-origin <img> loads.
function avatarUrl(state: TimestopState): string {
    const id = state.currentUser?.id;
    if (id == null) return '';
    return '/api/avatar?u=' + encodeURIComponent(String(id));
}

// initialText: get the 1-letter initial for the user, used as a
// fallback when no avatar is set and as the alt text on the <img>.
function initialText(state: TimestopState): string {
    const username = (state.currentUser?.username || '?').trim();
    return username.slice(0, 1).toUpperCase() || '?';
}

// renderImg: swap the avatar <div> for an <img>. Used by both the
// topbar tile and the Profile-modal tile.
function renderImgInto(target: HTMLElement, state: TimestopState): void {
    if (!state.currentUser?.avatar) return;
    // Preserve the wrapper classes (size, padding, color) and just
    // swap the inner content for the <img>. We use object-cover so
    // the image fills the square even if the user uploaded a tall or
    // wide picture.
    const url = avatarUrl(state) + '&v=' + encodeURIComponent(state.currentUser.avatar);
    const alt = initialText(state);
    // Cache-bust via the avatar filename. The server's ETag also
    // 304s on conditional GET, so the busting is only "live" on the
    // first load after an upload; subsequent reloads are 304s.
    target.innerHTML = '<img src="' + url + '" alt="' + alt + '" class="w-full h-full object-cover rounded-2xl" />';
}

function renderInitialInto(target: HTMLElement, state: TimestopState): void {
    target.textContent = initialText(state);
}

// paintUserBadge: re-render both the topbar tile AND the profile-modal
// tile to reflect state.currentUser.avatar. Idempotent — safe to call
// after every state change. Does nothing if the user is unauthed.
export function paintUserBadge(state: TimestopState): void {
    if (!state.currentUser) return;
    const hasAvatar = !!state.currentUser.avatar;
    const badge = findBadgeTile();
    if (badge) {
        if (hasAvatar) renderImgInto(badge, state);
        else renderInitialInto(badge, state);
    }
    const profile = findProfileInitial();
    if (profile) {
        if (hasAvatar) renderImgInto(profile, state);
        else renderInitialInto(profile, state);
    }
}

// Update state.currentUser.avatar in-place and repaint. Called by
// the upload flow's success path so the badge reflects the change
// without a /me round-trip.
export function applyUploadedAvatar(state: TimestopState, filename: string | null): void {
    if (!state.currentUser) return;
    state.currentUser.avatar = filename;
    paintUserBadge(state);
}

// =====================================================================
// 2. Cropper modal
// =====================================================================

const MAX_DIM = 512;        // server caps at 512; client mirrors.
const CROP_SIZE = 320;      // visible square in the crop modal.

type CropState = {
    img: HTMLImageElement;
    // Bounding box (in image coordinates) of the selected square.
    // The crop window is always a square covering this region; we
    // store the centre + side in image pixels.
    cx: number;
    cy: number;
    side: number;
    // Drag bookkeeping.
    dragging: boolean;
    dragMode: 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | null;
    dragStart: { mx: number; my: number; cx: number; cy: number; side: number };
};

let _crop: CropState | null = null;

function getCropModal(): HTMLElement | null {
    return document.getElementById('avatar-crop-modal');
}

function openCropModal(): void {
    const m = getCropModal();
    if (m) m.classList.remove('hidden');
}

function closeCropModal(): void {
    const m = getCropModal();
    if (m) m.classList.add('hidden');
    _crop = null;
}

// Show / hide the in-modal status line.
function setCropStatus(text: string, kind: 'err' | 'ok' | 'info' = 'info'): void {
    const el = document.getElementById('avatar-crop-status');
    if (!el) return;
    el.textContent = text;
    el.className = 'text-xs mt-2 min-h-[1em] ' +
        (kind === 'err' ? 'text-red-600'
            : kind === 'ok' ? 'text-emerald-600'
            : 'text-gray-500');
}

// File -> loaded HTMLImageElement. Resolves null on decode failure.
function loadImageFromFile(file: File): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onerror = () => resolve(null);
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => resolve(null);
            img.onload = () => resolve(img);
            img.src = String(reader.result || '');
        };
        reader.readAsDataURL(file);
    });
}

// Initialize the crop state for a freshly-loaded image. The crop
// window starts as the largest centred square that fits the image.
function initCropState(img: HTMLImageElement): void {
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w <= 0 || h <= 0) return;
    const side = Math.min(w, h);
    _crop = {
        img,
        cx: w / 2,
        cy: h / 2,
        side,
        dragging: false,
        dragMode: null,
        dragStart: { mx: 0, my: 0, cx: 0, cy: 0, side: 0 },
    };
}

// Render the crop overlay onto a <canvas>. The canvas is fixed at
// CROP_SIZE x CROP_SIZE and shows the selected square of the image,
// stretched to fit.
function paintCropCanvas(): void {
    const canvas = document.getElementById('avatar-crop-canvas') as HTMLCanvasElement | null;
    if (!canvas || !_crop) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;

    const w = _crop.img.naturalWidth || _crop.img.width;
    const h = _crop.img.naturalHeight || _crop.img.height;
    if (w <= 0 || h <= 0) return;

    // The source rectangle in image coordinates.
    const half = _crop.side / 2;
    const sx = Math.max(0, Math.min(w - _crop.side, _crop.cx - half));
    const sy = Math.max(0, Math.min(h - _crop.side, _crop.cy - half));
    // Draw a black background first so areas outside the source rect
    // (e.g. if the user dragged past the edge) read as black, not
    // transparent.
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);
    ctx.drawImage(_crop.img, sx, sy, _crop.side, _crop.side, 0, 0, CROP_SIZE, CROP_SIZE);

    // Update the live preview tile to mirror the crop.
    const preview = document.getElementById('avatar-crop-preview') as HTMLImageElement | HTMLCanvasElement | null;
    if (preview && 'width' in preview) {
        (preview as HTMLCanvasElement).width = CROP_SIZE;
        (preview as HTMLCanvasElement).height = CROP_SIZE;
        const pctx = (preview as HTMLCanvasElement).getContext('2d');
        if (pctx) {
            pctx.fillStyle = '#000';
            pctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);
            pctx.drawImage(_crop.img, sx, sy, _crop.side, _crop.side, 0, 0, CROP_SIZE, CROP_SIZE);
        }
    }
}

// Bind the drag handlers. Idempotent — the marker attribute on the
// canvas makes re-binding a no-op.
function bindCropDragHandlers(): void {
    const canvas = document.getElementById('avatar-crop-canvas');
    if (!canvas) return;
    if (canvas.hasAttribute('data-bound')) return;
    canvas.setAttribute('data-bound', '1');

    // Hit-test: are we near a corner handle, the inside, or empty?
    // Returns the drag mode for the click point, or null.
    function hitTest(clientX: number, clientY: number): 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | null {
        if (!_crop) return null;
        const rect = canvas!.getBoundingClientRect();
        // Map client coords to image coords. The canvas shows the
        // whole image stretched to CROP_SIZE; we need to invert.
        const w = _crop.img.naturalWidth || _crop.img.width;
        const h = _crop.img.naturalHeight || _crop.img.height;
        const scaleX = w / CROP_SIZE;
        const scaleY = h / CROP_SIZE;
        const ix = (clientX - rect.left) * scaleX;
        const iy = (clientY - rect.top) * scaleY;
        const half = _crop.side / 2;
        const minX = _crop.cx - half;
        const minY = _crop.cy - half;
        const maxX = _crop.cx + half;
        const maxY = _crop.cy + half;
        // Inside the square?
        if (ix >= minX && ix <= maxX && iy >= minY && iy <= maxY) {
            return 'move';
        }
        // Near a corner? Use a 12px-radius hit zone in client pixels.
        const px = 12 * scaleX;
        const py = 12 * scaleY;
        if (Math.abs(ix - minX) < px && Math.abs(iy - minY) < py) return 'resize-tl';
        if (Math.abs(ix - maxX) < px && Math.abs(iy - minY) < py) return 'resize-tr';
        if (Math.abs(ix - minX) < px && Math.abs(iy - maxY) < py) return 'resize-bl';
        if (Math.abs(ix - maxX) < px && Math.abs(iy - maxY) < py) return 'resize-br';
        return null;
    }

    canvas.addEventListener('mousedown', (ev) => {
        if (!_crop) return;
        const mode = hitTest(ev.clientX, ev.clientY);
        if (!mode) return;
        _crop.dragging = true;
        _crop.dragMode = mode;
        _crop.dragStart = {
            mx: ev.clientX,
            my: ev.clientY,
            cx: _crop.cx,
            cy: _crop.cy,
            side: _crop.side,
        };
        ev.preventDefault();
    });
    document.addEventListener('mousemove', (ev) => {
        if (!_crop || !_crop.dragging) return;
        const w = _crop.img.naturalWidth || _crop.img.width;
        const h = _crop.img.naturalHeight || _crop.img.height;
        // Translate client-pixel delta to image-pixel delta.
        const scaleX = w / CROP_SIZE;
        const scaleY = h / CROP_SIZE;
        const dx = (ev.clientX - _crop.dragStart.mx) * scaleX;
        const dy = (ev.clientY - _crop.dragStart.my) * scaleY;
        if (_crop.dragMode === 'move') {
            _crop.cx = _crop.dragStart.cx + dx;
            _crop.cy = _crop.dragStart.cy + dy;
        } else {
            // Resize: anchor the opposite corner; the new side is the
            // distance from the anchor to the new mouse position.
            const w0 = _crop.img.naturalWidth || _crop.img.width;
            const h0 = _crop.img.naturalHeight || _crop.img.height;
            const side0 = _crop.dragStart.side;
            const cx0 = _crop.dragStart.cx;
            const cy0 = _crop.dragStart.cy;
            // Anchor depends on which corner is being dragged.
            let ax: number, ay: number, dirX: number, dirY: number;
            if (_crop.dragMode === 'resize-br') { ax = cx0 - side0 / 2; ay = cy0 - side0 / 2; dirX = 1; dirY = 1; }
            else if (_crop.dragMode === 'resize-tr') { ax = cx0 - side0 / 2; ay = cy0 + side0 / 2; dirX = 1; dirY = -1; }
            else if (_crop.dragMode === 'resize-bl') { ax = cx0 + side0 / 2; ay = cy0 - side0 / 2; dirX = -1; dirY = 1; }
            else { ax = cx0 + side0 / 2; ay = cy0 + side0 / 2; dirX = -1; dirY = -1; }
            // Mouse pos in image coords.
            const rect = canvas.getBoundingClientRect();
            const mxImg = (ev.clientX - rect.left) * scaleX;
            const myImg = (ev.clientY - rect.top) * scaleY;
            // New side is the distance from the anchor in the drag
            // direction, projected to be positive and capped to the
            // smaller of the two image dimensions.
            const rawSide = dirX * (mxImg - ax);
            const rawSide2 = dirY * (myImg - ay);
            // Take the smaller of the two so the new region stays
            // inside the image.
            const newSide = Math.max(16, Math.min(Math.min(w0, h0), Math.min(rawSide, rawSide2)));
            _crop.side = newSide;
            // Re-anchor centre so the opposite corner doesn't move.
            _crop.cx = ax + dirX * newSide / 2;
            _crop.cy = ay + dirY * newSide / 2;
        }
        // Clamp the crop window inside the image bounds.
        const halfS = _crop.side / 2;
        _crop.cx = Math.max(halfS, Math.min(w - halfS, _crop.cx));
        _crop.cy = Math.max(halfS, Math.min(h - halfS, _crop.cy));
        paintCropCanvas();
    });
    document.addEventListener('mouseup', () => {
        if (!_crop) return;
        _crop.dragging = false;
        _crop.dragMode = null;
    });
}

// =====================================================================
// 3. Public entry points
// =====================================================================

// openAvatarPicker: invoked from the Profile modal's avatar tile.
// Creates a hidden <input type="file">, clicks it, and on a chosen
// file opens the cropper.
export function openAvatarPicker(): void {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'image/jpeg,image/png,image/webp';
    inp.style.display = 'none';
    inp.addEventListener('change', () => {
        const f = inp.files && inp.files[0];
        if (!f) return;
        // Clear the input so re-selecting the SAME file re-fires.
        // (The DOM only emits 'change' on value mutation.)
        inp.value = '';
        void openAvatarCropperFor(f);
    });
    document.body.appendChild(inp);
    inp.click();
    // Clean up after the picker is dismissed. We can't reliably
    // know when the dialog closes across browsers, so a setTimeout
    // is the simplest correct approach.
    setTimeout(() => { inp.remove(); }, 60_000);
}

// openAvatarCropperFor: take a File, decode it, open the modal, and
// show the crop UI. Used by openAvatarPicker() and is also exported
// so tests / future code can drop a file in.
export async function openAvatarCropperFor(file: File): Promise<void> {
    if (file.size > 8 * 1024 * 1024) {
        setCropStatus(t('profile.avatar_too_large'), 'err');
        return;
    }
    setCropStatus(t('profile.avatar_loading'), 'info');
    const img = await loadImageFromFile(file);
    if (!img) {
        setCropStatus(t('profile.avatar_read_failed'), 'err');
        return;
    }
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (w <= 0 || h <= 0 || w > 8000 || h > 8000) {
        setCropStatus(t('profile.avatar_dimensions_out_of_range'), 'err');
        return;
    }
    initCropState(img);
    bindCropDragHandlers();
    openCropModal();
    setCropStatus(t('profile.avatar_drag_help'), 'info');
    paintCropCanvas();
}

// closeAvatarCropper: hide the modal and discard the in-memory image.
export function closeAvatarCropper(): void {
    closeCropModal();
    _crop = null;
}

// confirmAvatarCrop: invoked from the modal's Confirm button. Renders
// the cropped region to a 512x512 canvas, encodes it to a Blob, then
// POSTs the Blob as multipart/form-data to /api/upload_avatar.
export async function confirmAvatarCrop(state: TimestopState, config: ApiFetchConfig): Promise<void> {
    if (!_crop) {
        setCropStatus(t('profile.avatar_nothing_to_confirm'), 'err');
        return;
    }
    const w = _crop.img.naturalWidth || _crop.img.width;
    const h = _crop.img.naturalHeight || _crop.img.height;
    if (w <= 0 || h <= 0) {
        setCropStatus(t('profile.avatar_empty'), 'err');
        return;
    }
    // Render to a 512x512 canvas. The browser will re-encode when we
    // call canvas.toBlob(); the server then re-encodes (lossless
    // round-trip for PNG, slight quality loss for JPEG/WebP) and
    // stores the bytes.
    const out = document.createElement('canvas');
    out.width = MAX_DIM;
    out.height = MAX_DIM;
    const ctx = out.getContext('2d');
    if (!ctx) {
        setCropStatus(t('profile.avatar_no_canvas'), 'err');
        return;
    }
    const half = _crop.side / 2;
    const sx = Math.max(0, Math.min(w - _crop.side, _crop.cx - half));
    const sy = Math.max(0, Math.min(h - _crop.side, _crop.cy - half));
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, MAX_DIM, MAX_DIM);
    ctx.drawImage(_crop.img, sx, sy, _crop.side, _crop.side, 0, 0, MAX_DIM, MAX_DIM);

    // Choose the encoding based on the file's MIME. PNG is
    // lossless and good for graphics; JPEG is smaller for photos.
    const sourceMime = (fileMimeFor(_crop.img) || 'image/png');
    const outType = sourceMime === 'image/jpeg' ? 'image/jpeg'
                  : sourceMime === 'image/webp' ? 'image/webp'
                  : 'image/png';
    const quality = outType === 'image/png' ? undefined : 0.9;

    const blob: Blob | null = await new Promise((resolve) => {
        out.toBlob((b) => resolve(b), outType, quality);
    });
    if (!blob) {
        setCropStatus(t('profile.avatar_encode_failed'), 'err');
        return;
    }

    const form = new FormData();
    // The filename is just advisory; the server overwrites it with
    // the md5 of the bytes. Use a stable extension so debugging
    // matches.
    const ext = outType === 'image/jpeg' ? 'jpg'
              : outType === 'image/webp' ? 'webp'
              : 'png';
    form.append('file', blob, 'avatar.' + ext);

    setCropStatus(t('profile.avatar_uploading'), 'info');
    try {
        const r = await apiFetch('upload_avatar', { method: 'POST', body: form }, config);
        if (!r.ok || !r.data) {
            const err = ((r.data as { error?: string } | null)?.error) || 'upload_failed';
            const msg = err === 'too_large' ? t('profile.avatar_too_large')
                      : err === 'unsupported_image' || err === 'decode_failed' ? t('profile.avatar_unsupported')
                      : err === 'gd_missing' ? t('profile.avatar_gd_missing')
                      : err === 'no_session' ? t('errors.session_expired')
                      : t('profile.avatar_upload_failed', { code: err });
            setCropStatus(msg, 'err');
            return;
        }
        const filename = (r.data as { avatar?: string | null }).avatar ?? null;
        applyUploadedAvatar(state, filename);
        // Also reflect in the Profile modal if it's currently open.
        const profile = findProfileInitial();
        if (profile && state.currentUser?.avatar) {
            renderImgInto(profile, state);
        }
        setCropStatus(t('profile.avatar_saved'), 'ok');
        // Auto-close after a brief moment so the user sees the success state.
        setTimeout(() => { closeAvatarCropper(); }, 600);
    } catch (e) {
        setCropStatus(t('profile.avatar_upload_failed', { code: (e as Error).message }), 'err');
    }
}

// fileMimeFor: best-effort lookup of the source image's MIME. The
// browser's FileReader gave us a data: URL; we sniff the prefix.
function fileMimeFor(img: HTMLImageElement): string {
    const src = img.src || '';
    if (src.startsWith('data:image/jpeg')) return 'image/jpeg';
    if (src.startsWith('data:image/webp')) return 'image/webp';
    return 'image/png';
}
